/* eslint-disable react/jsx-key */
import React, { useState, useCallback, useEffect } from 'react';
import { useQuery } from 'react-query';
import { Button, Form, Input, List, message } from 'antd-v5';
import { SettingsContent } from '../../../components/SettingsContent';
import {
  createAppointmentType,
  getAppointmentTypes,
  updateAppointmentType,
  deleteAppointmentType,
} from '../../../../../../services/appointments';

export const AppointmentTypes = () => {
  const [openFooter, setOpenFooter] = useState(false);
  const [form] = Form.useForm();
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [itemOpen, setItemOpen] = useState(null);

  const {
    data: appointmentTypes,
    isFetching,
    refetch,
  } = useQuery(
    'appointmentTypes',
    async () => {
      return getAppointmentTypes();
    },
    {
      refetchOnWindowFocus: false,
      initialData: [],
    },
  );

  const handleSuccess = useCallback(
    async data => {
      try {
        setConfirmLoading(true);
        await createAppointmentType(data);
        message.success('Appointment type created successfully');
        refetch();
        setItemOpen(null);
        setOpenFooter(false);
        form.resetFields();
      } catch (error) {
        const msg = error?.response?.status === 409 ? error.response.data.message : 'Error creating appointment type';
        message.error(msg);
      } finally {
        setConfirmLoading(false);
      }
    },
    [refetch],
  );

  const handleCancel = useCallback(() => {
    setOpenFooter(false);
    form.resetFields();
  }, [form]);

  return (
    <SettingsContent
      title="Appointments Types"
      description="Appointment types your clients can choose from. These appear on the appointment form."
      data-testid="appointments__types--section"
    >
      <List
        className="w-100-P"
        size="small"
        dataSource={appointmentTypes}
        renderItem={item => (
          <AppointmentTypeItem
            item={item}
            refetch={refetch}
            open={itemOpen && itemOpen === item.id}
            setItemOpen={setItemOpen}
          />
        )}
        loading={isFetching}
        footer={
          openFooter && (
            <Form onFinish={handleSuccess} layout="inline" className="flex-row items-center gap-16" form={form}>
              <Form.Item noStyle name="name" rules={[{ required: true }]}>
                <Input placeholder="Appointment Type" style={{ maxWidth: '360px', width: '100%', marginRight: 0 }} />
              </Form.Item>
              <Button loading={confirmLoading} type="primary" htmlType="submit">
                Save
              </Button>
              <Button onClick={handleCancel} type="default" disabled={confirmLoading} htmlType="button">
                Cancel
              </Button>
            </Form>
          )
        }
      />
      <Button type="primary" className="m-top-16" onClick={() => setOpenFooter(true)}>
        Add Appointment Type
      </Button>
    </SettingsContent>
  );
};

const AppointmentTypeItem = ({ item, open, setItemOpen, refetch }) => {
  const [form] = Form.useForm();
  const [confirmLoading, setConfirmLoading] = useState(false);

  useEffect(() => {
    form.setFieldValue('name', item.name);
  }, [item.name]);

  const handleSuccess = useCallback(
    async data => {
      try {
        setConfirmLoading(true);
        await updateAppointmentType(item.id, data);
        message.success('Appointment type updated successfully');
        setItemOpen(null);
      } catch (error) {
        const msg = error?.response?.status === 409 ? error.response.data.message : 'Error updating appointment type';
        message.error(msg);
      } finally {
        refetch();
        setConfirmLoading(false);
      }
    },
    [item.id, refetch],
  );

  const handleDelete = useCallback(async () => {
    try {
      setConfirmLoading(true);
      await deleteAppointmentType(item.id);
      message.success('Appointment type archived successfully');
    } catch (error) {
      console.log(error);
      message.error('Error archiving appointment type');
    } finally {
      refetch();
      setConfirmLoading(false);
    }
  }, [item.id, refetch]);

  const handleCancel = useCallback(() => {
    setItemOpen(null);
    form.setFieldValue('name', item.name);
  }, [form, setItemOpen, item.name]);

  return (
    <List.Item
      styles={{ actions: { margin: 0 } }}
      actions={
        !open
          ? [
              <Button onClick={() => setItemOpen(item.id)} variant="text" color="primary" htmlType="button">
                Edit
              </Button>,
          ]
          : [
              <Button
                onClick={() => handleSuccess(form.getFieldsValue())}
                variant="text"
                color="primary"
                htmlType="button"
                loading={confirmLoading}
              >
                Save
              </Button>,
              <Button onClick={handleDelete} variant="text" color="danger" htmlType="button" disabled={confirmLoading}>
                Archive
              </Button>,
              <Button onClick={handleCancel} variant="text" color="danger" htmlType="button" disabled={confirmLoading}>
                Cancel
              </Button>,
          ]
      }
      key={item.id}
    >
      <Form layout="inline" className="flex-row items-center gap-16" form={form} disabled={confirmLoading}>
        <Form.Item noStyle name="name" rules={[{ required: true }]}>
          {open ? (
            <Input
              placeholder="Appointment Type"
              onPressEnter={() => handleSuccess(form.getFieldsValue())}
              style={{ maxWidth: '360px', width: '100%', marginRight: 0 }}
            />
          ) : (
            item.name
          )}
        </Form.Item>
      </Form>
    </List.Item>
  );
};
